/**
 * GPX File Processing Library
 * Handles GPX file parsing, distance calculations, elevation smoothing, and auto-segmentation
 */

export interface GpxTrackPoint {
  lat: number
  lon: number
  ele?: number
  time?: Date
}

export interface GpxSummary {
  totalDistance: number
  totalElevationGain: number
  totalElevationLoss: number
  trackPoints: number
  startPoint: GpxTrackPoint
  endPoint: GpxTrackPoint
  rawTrackPoints: GpxTrackPoint[]
}

export interface AutoSegmentOptions {
  mode: 'every-1km' | 'every-5km' | 'every-10km' | 'custom'
  aidStations?: Array<{ distance: number; name: string }>
}

export interface GeneratedSegment {
  label: string
  distance_km: number
  elev_gain_m: number
  elev_loss_m: number
  order_index: number
}

export interface GpxValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Validates a GPX file before processing
 */
export async function validateGpxFile(file: File): Promise<GpxValidationResult> {
  try {
    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      return { isValid: false, error: 'File too large - maximum 50MB allowed' }
    }

    // Check file extension
    if (!file.name.toLowerCase().endsWith('.gpx')) {
      return { isValid: false, error: 'Invalid file type - only GPX files are supported' }
    }

    // Quick XML validation
    const text = await file.text()
    if (!text.includes('<gpx') || !text.includes('</gpx>')) {
      return { isValid: false, error: 'Invalid GPX file format' }
    }

    return { isValid: true }
  } catch (error) {
    return { isValid: false, error: 'Unable to read file' }
  }
}

/**
 * Parses a GPX file and extracts track points with distance and elevation data
 */
export async function parseGpxFile(file: File): Promise<GpxSummary> {
  const validation = await validateGpxFile(file)
  if (!validation.isValid) {
    throw new Error(validation.error)
  }

  const text = await file.text()
  const parser = new DOMParser()
  const doc = parser.parseFromString(text, 'text/xml')

  // Check for parser errors
  const parseError = doc.querySelector('parsererror')
  if (parseError) {
    throw new Error('Invalid GPX file format - unable to parse XML')
  }

  // Extract track points from all tracks and track segments
  const trackPoints: GpxTrackPoint[] = []
  const trksegs = doc.querySelectorAll('trk trkseg')

  if (trksegs.length === 0) {
    throw new Error('No track segments found in GPX file')
  }

  trksegs.forEach(trkseg => {
    const trkpts = trkseg.querySelectorAll('trkpt')
    trkpts.forEach(trkpt => {
      const lat = parseFloat(trkpt.getAttribute('lat') || '0')
      const lon = parseFloat(trkpt.getAttribute('lon') || '0')
      
      if (isNaN(lat) || isNaN(lon)) {
        return // Skip invalid coordinates
      }

      const eleElement = trkpt.querySelector('ele')
      const timeElement = trkpt.querySelector('time')
      
      const point: GpxTrackPoint = { lat, lon }
      
      if (eleElement) {
        const ele = parseFloat(eleElement.textContent || '0')
        if (!isNaN(ele)) {
          point.ele = ele
        }
      }
      
      if (timeElement) {
        const timeStr = timeElement.textContent
        if (timeStr) {
          point.time = new Date(timeStr)
        }
      }
      
      trackPoints.push(point)
    })
  })

  if (trackPoints.length < 2) {
    throw new Error('GPX file must contain at least 2 track points')
  }

  // Downsample if too many points for performance
  const processedTrackPoints = trackPoints.length > 50000 
    ? downsampleTrackPoints(trackPoints, 50000)
    : trackPoints

  // Smooth elevation data if available
  const smoothedTrackPoints = smoothElevation(processedTrackPoints)

  // Calculate distance and elevation statistics
  const summary = calculateGpxSummary(smoothedTrackPoints)

  // Validate minimum course distance
  if (summary.totalDistance < 1.0) {
    throw new Error('Course too short for auto-segmentation (minimum 1km)')
  }

  return summary
}

/**
 * Downsamples track points to improve performance for large files
 */
function downsampleTrackPoints(trackPoints: GpxTrackPoint[], maxPoints: number): GpxTrackPoint[] {
  if (trackPoints.length <= maxPoints) {
    return trackPoints
  }

  const step = trackPoints.length / maxPoints
  const downsampled: GpxTrackPoint[] = []
  
  for (let i = 0; i < trackPoints.length; i += step) {
    downsampled.push(trackPoints[Math.floor(i)])
  }

  // Always include the last point
  if (downsampled[downsampled.length - 1] !== trackPoints[trackPoints.length - 1]) {
    downsampled.push(trackPoints[trackPoints.length - 1])
  }

  return downsampled
}

/**
 * Applies rolling median smoothing to elevation data
 */
export function smoothElevation(trackPoints: GpxTrackPoint[], windowSize: number = 11): GpxTrackPoint[] {
  if (trackPoints.length === 0 || !trackPoints.some(p => p.ele !== undefined)) {
    return trackPoints
  }

  const smoothed = [...trackPoints]
  const halfWindow = Math.floor(windowSize / 2)

  for (let i = 0; i < trackPoints.length; i++) {
    if (trackPoints[i].ele === undefined) continue

    const start = Math.max(0, i - halfWindow)
    const end = Math.min(trackPoints.length - 1, i + halfWindow)
    
    const elevations: number[] = []
    for (let j = start; j <= end; j++) {
      if (trackPoints[j].ele !== undefined) {
        elevations.push(trackPoints[j].ele!)
      }
    }

    if (elevations.length > 0) {
      elevations.sort((a, b) => a - b)
      const medianIndex = Math.floor(elevations.length / 2)
      smoothed[i] = {
        ...trackPoints[i],
        ele: elevations[medianIndex]
      }
    }
  }

  return smoothed
}

/**
 * Calculates total distance using Haversine formula
 */
export function calculateHaversineDistance(point1: GpxTrackPoint, point2: GpxTrackPoint): number {
  const R = 6371 // Earth's radius in kilometers
  const dLat = toRadians(point2.lat - point1.lat)
  const dLon = toRadians(point2.lon - point1.lon)
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.lat)) * Math.cos(toRadians(point2.lat)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

/**
 * Calculates GPX summary statistics
 */
function calculateGpxSummary(trackPoints: GpxTrackPoint[]): GpxSummary {
  let totalDistance = 0
  let totalElevationGain = 0
  let totalElevationLoss = 0

  // Calculate cumulative distance
  for (let i = 1; i < trackPoints.length; i++) {
    totalDistance += calculateHaversineDistance(trackPoints[i - 1], trackPoints[i])
  }

  // Calculate elevation gain/loss
  for (let i = 1; i < trackPoints.length; i++) {
    const prevEle = trackPoints[i - 1].ele
    const currEle = trackPoints[i].ele
    
    if (prevEle !== undefined && currEle !== undefined) {
      const elevChange = currEle - prevEle
      if (elevChange > 0) {
        totalElevationGain += elevChange
      } else {
        totalElevationLoss += Math.abs(elevChange)
      }
    }
  }

  return {
    totalDistance: Math.round(totalDistance * 100) / 100, // Round to 2 decimals
    totalElevationGain: Math.round(totalElevationGain),
    totalElevationLoss: Math.round(totalElevationLoss),
    trackPoints: trackPoints.length,
    startPoint: trackPoints[0],
    endPoint: trackPoints[trackPoints.length - 1],
    rawTrackPoints: trackPoints
  }
}

/**
 * Generates segments based on the selected auto-segmentation mode
 */
export function generateSegments(gpxData: GpxSummary, options: AutoSegmentOptions): GeneratedSegment[] {
  const { mode } = options
  const trackPoints = gpxData.rawTrackPoints

  let intervalKm: number
  switch (mode) {
    case 'every-1km':
      intervalKm = 1.0
      break
    case 'every-5km':
      intervalKm = 5.0
      break
    case 'every-10km':
      intervalKm = 10.0
      break
    default:
      throw new Error(`Unsupported segmentation mode: ${mode}`)
  }

  const segments: GeneratedSegment[] = []
  let cumulativeDistance = 0
  let segmentStartIndex = 0
  let segmentIndex = 0

  // Find segment boundaries at interval distances
  for (let i = 1; i < trackPoints.length; i++) {
    const segmentDistance = calculateHaversineDistance(trackPoints[i - 1], trackPoints[i])
    cumulativeDistance += segmentDistance

    // Check if we've reached the next interval or the end of the track
    if (cumulativeDistance >= intervalKm || i === trackPoints.length - 1) {
      const segmentTrackPoints = trackPoints.slice(segmentStartIndex, i + 1)
      const segment = createSegmentFromTrackPoints(segmentTrackPoints, segmentIndex)
      segments.push(segment)

      segmentStartIndex = i
      segmentIndex++
      cumulativeDistance = 0
    }
  }

  // If the last segment is very short (<0.5km), merge it with the previous one
  if (segments.length > 1 && segments[segments.length - 1].distance_km < 0.5) {
    const lastSegment = segments.pop()!
    const prevSegment = segments[segments.length - 1]
    
    prevSegment.distance_km = Math.round((prevSegment.distance_km + lastSegment.distance_km) * 100) / 100
    prevSegment.elev_gain_m += lastSegment.elev_gain_m
    prevSegment.elev_loss_m += lastSegment.elev_loss_m
  }

  return segments
}

/**
 * Creates a segment from a range of track points
 */
function createSegmentFromTrackPoints(trackPoints: GpxTrackPoint[], segmentIndex: number): GeneratedSegment {
  let distance = 0
  let elevationGain = 0
  let elevationLoss = 0

  // Calculate segment distance
  for (let i = 1; i < trackPoints.length; i++) {
    distance += calculateHaversineDistance(trackPoints[i - 1], trackPoints[i])
  }

  // Calculate segment elevation gain/loss
  for (let i = 1; i < trackPoints.length; i++) {
    const prevEle = trackPoints[i - 1].ele
    const currEle = trackPoints[i].ele
    
    if (prevEle !== undefined && currEle !== undefined) {
      const elevChange = currEle - prevEle
      if (elevChange > 0) {
        elevationGain += elevChange
      } else {
        elevationLoss += Math.abs(elevChange)
      }
    }
  }

  return {
    label: `Segment ${segmentIndex + 1}`,
    distance_km: Math.round(distance * 100) / 100,
    elev_gain_m: Math.round(elevationGain),
    elev_loss_m: Math.round(elevationLoss),
    order_index: segmentIndex
  }
}

/**
 * Processes GPX file with progress callback for large files
 */
export async function processGpxFileWithProgress(
  file: File,
  onProgress?: (percent: number) => void
): Promise<GpxSummary> {
  onProgress?.(10) // Start progress

  const summary = await parseGpxFile(file)
  onProgress?.(100) // Complete

  return summary
}